/**
 * The match record and its scoreboard (BUILDLIST 67.14, the owner: "a scoreboard for lab / skirmish games … at least at
 * game end would be cool if accessible at pause"). One record of what happened, kept by the Match beside its state
 * (not in it: nothing here feeds back into play, so no outcome moves and no checksum changes) and fed from the action
 * clock's own events: shots from `fired`, hits and kills from `hitUnit` (credited once, to the bullet's owner), a
 * rocket's `blast` and a seat's `shellImpact` (their kills credited to whoever fired), captures from the objective
 * layer (`captured`, credited to the capturing team's units standing on the point), deaths from units that were alive
 * before a step and are not after it (whatever the cause). The table reads the record and the state; the end-of-game
 * card adds the winner, how, and the match's shape in a line. The sector's battle report reads the same record at the
 * seam. Pure.
 */
import type { GameState, PlayerState } from '../types';
import type { CapturePoint } from '../types';
import { standingOn } from './capturePoints';
import { mountedStructure } from './seats';
import { PHYS_DT, TEAMS } from '../constants';

export interface UnitScore { kills: number; deaths: number; captures: number; shots: number; hits: number }

export interface MatchRecord {
  units: Record<number, UnitScore>;
  /** Units alive after the last recorded step (for deaths of any cause). */
  alive: number[];
  captures: Array<{ tick: number; pointId: number; team: number }>;
  /** Who holds each point since when (ticks), and the longest unbroken hold so far. */
  holds: Record<number, { team: number; since: number }>;
  longestHold: { pointId: number; team: number; ticks: number } | null;
  shells: number;
  firstKill: { tick: number; by: number; victim: number } | null;
  /** 68.7: the tick each unit last fired, and last was hit (what "in contact" reads). */
  lastShot: Record<number, number>;
  lastHurt: Record<number, number>;
  /** 67.53 (the owner: "cumulative player controlled counts … In case you switch units"): the human's totals over every unit a
   *  human seat held, each event credited to whoever was held at its tick; `held` the units held after the last recorded
   *  step (the Match's controllers), `everHeld` whether any was. */
  you: UnitScore;
  held: number[];
  everHeld: boolean;
}

/** The units a human seat holds now (the Match's controllers), in id order. */
export function heldIds(state: Pick<GameState, 'controllers'>): number[] {
  return Object.keys(state.controllers ?? {}).map(Number).sort((a, b) => a - b);
}

/** Has the held set changed since the record last read it (a step with no events still has to note a switch)? */
export function heldChanged(rec: MatchRecord, state: Pick<GameState, 'controllers'>): boolean {
  const now = heldIds(state);
  return now.length !== rec.held.length || now.some((id, i) => id !== rec.held[i]);
}

/** The kinds of event the record reads (a structural subset of the Match's ActionEvent). */
export type RecordEvent =
  | { kind: 'fired'; unitId: number }
  | { kind: 'hitUnit'; bullet: { ownerId: number }; targetId: number; killed: boolean }
  | { kind: 'blast'; ownerId: number; kills: number[] }
  | { kind: 'shellFired'; unitId: number }
  | { kind: 'shellImpact'; shell: { ownerId: number }; resolution: { kills: number[] } }
  | { kind: 'captured'; pointId: number; team: number }
  | { kind: string };

const zero = (): UnitScore => ({ kills: 0, deaths: 0, captures: 0, shots: 0, hits: 0 });

export function emptyRecord(state: Pick<GameState, 'players'> & Partial<Pick<GameState, 'controllers'>>): MatchRecord {
  const held = heldIds(state);
  return { you: zero(), held, everHeld: held.length > 0, units: Object.fromEntries(state.players.map(p => [p.id, zero()])), alive: state.players.filter(p => p.alive).map(p => p.id), captures: [], holds: {}, longestHold: null, shells: 0, firstKill: null, lastShot: {}, lastHurt: {} };
}

/** The record after one step's events, read against the state after that step. */
export function recordEvents(rec: MatchRecord, events: readonly RecordEvent[], after: GameState, tick: number): MatchRecord {
  const units: Record<number, UnitScore> = { ...rec.units };
  // 67.53: what a unit the human held as this step began does is the human's too (`held`: the record's, from the last step)
  const heldNow = new Set(rec.held);
  let you = rec.you;
  const bump = (id: number, k: keyof UnitScore, n = 1) => {
    units[id] = { ...(units[id] ?? zero()), [k]: (units[id]?.[k] ?? 0) + n };
    if (heldNow.has(id)) you = { ...you, [k]: you[k] + n };
  };
  let { captures, holds, longestHold, shells, firstKill } = rec;
  let lastShot = rec.lastShot, lastHurt = rec.lastHurt;
  const shot = (id: number) => { if (lastShot === rec.lastShot) lastShot = { ...lastShot }; lastShot[id] = tick; };
  const hurt = (id: number) => { if (lastHurt === rec.lastHurt) lastHurt = { ...lastHurt }; lastHurt[id] = tick; };
  const kill = (by: number, victim: number) => {
    const killer = after.players.find(p => p.id === by);
    const dead = after.players.find(p => p.id === victim);
    if (!killer || !dead || killer.teamIndex === dead.teamIndex) return;   // no credit for a teammate or oneself
    bump(by, 'kills');
    if (!firstKill) firstKill = { tick, by, victim };
  };
  for (const e of events) {
    if (e.kind === 'fired' && 'unitId' in e) { bump(e.unitId, 'shots'); shot(e.unitId); }
    else if (e.kind === 'hitUnit' && 'bullet' in e) { bump(e.bullet.ownerId, 'hits'); hurt(e.targetId); if (e.killed) kill(e.bullet.ownerId, e.targetId); }
    else if (e.kind === 'blast' && 'kills' in e) { for (const d of (e as { damages?: Array<{ playerId: number }> }).damages ?? []) hurt(d.playerId); for (const v of e.kills) kill(e.ownerId, v); }
    else if (e.kind === 'shellFired' && 'unitId' in e) { bump(e.unitId, 'shots'); shells++; shot(e.unitId); }
    else if (e.kind === 'shellImpact' && 'shell' in e) { for (const v of e.resolution.kills) kill(e.shell.ownerId, v); }
    else if (e.kind === 'captured' && 'pointId' in e && 'team' in e) {
      captures = [...captures, { tick, pointId: e.pointId, team: e.team }];
      const site = after.modeState.points?.sites.find(s => s.id === e.pointId) as CapturePoint | undefined;
      if (site) {
        // credited to the capturing team's units standing on the point
        const r = standingOn(after, site);
        if (r.get(e.team)) for (const p of onPoint(after, site, e.team)) bump(p.id, 'captures');
      }
      // the hold that ended, and the one that starts
      const prev = holds[e.pointId];
      if (prev && (!longestHold || tick - prev.since > longestHold.ticks)) longestHold = { pointId: e.pointId, team: prev.team, ticks: tick - prev.since };
      holds = { ...holds, [e.pointId]: { team: e.team, since: tick } };
    }
  }
  // deaths, whatever the cause: alive before this step, not after it — or brought back by a wave within it (killed on a
  // wave's tick, a unit dies and respawns in one step: it is alive on both sides of it)
  const nowAlive = after.players.filter(p => p.alive).map(p => p.id);
  const now = new Set(nowAlive);
  const respawned = new Set(events.filter(e => e.kind === 'respawned' && 'unitId' in e).map(e => (e as { unitId: number }).unitId));
  for (const id of rec.alive) if (!now.has(id) || respawned.has(id)) bump(id, 'deaths');
  const held = heldIds(after);
  return { units, alive: nowAlive, captures, holds, longestHold, shells, firstKill, lastShot, lastHurt, you, held, everHeld: rec.everHeld || held.length > 0 };
}

/** The capturing team's units on a point (standingOn's rule, by unit). */
function onPoint(state: GameState, site: CapturePoint, team: number): PlayerState[] {
  const before = standingOn(state, site).get(team) ?? 0;
  if (!before) return [];
  return state.players.filter(p => p.alive && !p.airborne && p.teamIndex === team && p.planetIndex === site.planetIndex && standingOn({ ...state, players: [p] }, site).get(team));
}

export type UnitStatus = 'alive' | 'seated' | 'held' | 'down';

export interface ScoreRow { id: number; name: string; team: number; status: UnitStatus; doing: string; you: boolean; score: UnitScore }
export interface TeamRow { team: number; name: string; tickets: number | null; points: number; alive: number; total: number; kills: number }
export interface ScoreTable { clock: string; teams: TeamRow[]; rows: ScoreRow[];
  /** 67.53: the human's totals over every unit they held (absent: none was held — watching). */
  you?: UnitScore }

/** The team's name, as the game names it (the companies; team names stay with the flavor layer off). */
export const teamName = (t: number): string => TEAMS[t % TEAMS.length]?.name ?? `Team ${t + 1}`;

const clockText = (seconds: number): string => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;

/** What a unit is doing now, in a word or two: in a seat, its stance, or moving. */
function doingOf(state: GameState, p: PlayerState, seated: boolean): string {
  if (!p.alive) return '—';
  if (seated) return 'manning a gun';
  if (p.airborne) return 'airborne';
  return p.stance && p.stance !== 'stand' ? p.stance : 'on foot';
}

/**
 * The table: teams with their tickets, points held and who stands; per unit its status, what it is doing and its
 * score; the viewer's unit (`heldId`) marked; sorted by team, then kills, then fewest deaths.
 */
export function scoreTable(state: GameState, rec: MatchRecord, heldId: number | null, tick: number): ScoreTable {
  const seated = new Set(state.players.filter(p => p.alive && mountedStructure(p, state)).map(p => p.id));
  const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
  const points = state.modeState.points?.sites ?? [];
  const tickets = state.modeState.respawn?.tickets;
  const teamRows: TeamRow[] = teams.map(t => {
    const mine = state.players.filter(p => p.teamIndex === t);
    return {
      team: t, name: teamName(t),
      tickets: tickets && tickets[t] !== undefined ? tickets[t] : null,
      points: points.filter(s => s.owner === t).length,
      alive: mine.filter(p => p.alive).length, total: mine.length,
      kills: mine.reduce((a, p) => a + (rec.units[p.id]?.kills ?? 0), 0),
    };
  });
  const rows: ScoreRow[] = state.players.map(p => {
    const isSeated = seated.has(p.id);
    const status: UnitStatus = !p.alive ? 'down' : p.id === heldId ? 'held' : isSeated ? 'seated' : 'alive';
    return { id: p.id, name: p.name, team: p.teamIndex, status, doing: doingOf(state, p, isSeated), you: p.id === heldId, score: rec.units[p.id] ?? zero() };
  }).sort((a, b) => a.team - b.team || b.score.kills - a.score.kills || a.score.deaths - b.score.deaths || a.id - b.id);
  return { clock: clockText(tick * PHYS_DT), teams: teamRows, rows, ...(rec.everHeld ? { you: rec.you } : {}) };
}

/**
 * The same table from a finished battle's per-unit record (the sector's `BattleOutcome.units`, which the battle's
 * match wrote): the sector's battle report shows it, so a battle's numbers read the same at the end card and at the
 * seam. Status is up or down; there is no "doing" once it is over; captures are not booked per unit there.
 */
export function tableFromUnits(sides: ReadonlyArray<{ team: number; name: string; units: ReadonlyArray<{ id: string; name: string }> }>, units: ReadonlyArray<{ unitId: string; alive: boolean; kills: number; shots: number; hits: number }>, viewerTeam: number | null, seconds: number): ScoreTable {
  const byId = new Map(units.map(u => [u.unitId, u] as const));
  const rows: ScoreRow[] = sides.flatMap(s => s.units.map((u, k) => {
    const o = byId.get(u.id);
    return { id: s.team * 1000 + k, name: u.name, team: s.team, status: (o?.alive ? 'alive' : 'down') as UnitStatus, doing: '—', you: false, score: { kills: o?.kills ?? 0, deaths: o && !o.alive ? 1 : 0, captures: 0, shots: o?.shots ?? 0, hits: o?.hits ?? 0 } };
  })).sort((a, b) => a.team - b.team || b.score.kills - a.score.kills || a.score.deaths - b.score.deaths || a.id - b.id);
  const teams: TeamRow[] = sides.map(s => {
    const mine = rows.filter(r => r.team === s.team);
    return { team: s.team, name: s.name + (s.team === viewerTeam ? ' (you)' : ''), tickets: null, points: 0, alive: mine.filter(r => r.status !== 'down').length, total: mine.length, kills: mine.reduce((a, r) => a + r.score.kills, 0) };
  });
  return { clock: clockText(seconds), teams, rows };
}

/** The match's shape in one line: length, shots and hits, kills, captures, the longest hold, shells. */
export function matchShape(state: GameState, rec: MatchRecord, tick: number): string {
  const all = Object.values(rec.units);
  const shots = all.reduce((a, u) => a + u.shots, 0), hits = all.reduce((a, u) => a + u.hits, 0), kills = all.reduce((a, u) => a + u.kills, 0);
  const parts = [`${clockText(tick * PHYS_DT)} of fighting`, `${shots} shot${shots === 1 ? '' : 's'}, ${hits} hit${hits === 1 ? '' : 's'}`, `${kills} kill${kills === 1 ? '' : 's'}`];
  if (rec.captures.length) parts.push(`${rec.captures.length} capture${rec.captures.length === 1 ? '' : 's'}`);
  // the longest hold, counting a hold still running
  let best = rec.longestHold;
  for (const [pid, h] of Object.entries(rec.holds)) if (!best || tick - h.since > best.ticks) best = { pointId: Number(pid), team: h.team, ticks: tick - h.since };
  if (best && best.ticks > 0) parts.push(`longest hold ${clockText(best.ticks * PHYS_DT)} (${teamName(best.team)}, point ${String.fromCharCode(65 + best.pointId)})`);
  if (rec.shells) parts.push(`${rec.shells} shell${rec.shells === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

/** How a match was won, in words (the win pipeline's reason). */
export function howWon(reason: string | null | undefined): string {
  switch (reason) {
    case 'elimination': return 'the other side was wiped out';
    case 'turnLimit': return 'time ran out; the score decided';
    case 'objective': return 'the objective (tickets or points)';
    case 'tickets': return 'the other side ran out of tickets';
    default: return reason ?? 'the match ended';
  }
}
